import { useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/DeleteOutlined'
import AddBusinessIcon from '@mui/icons-material/AddBusiness'
import BusinessIcon from '@mui/icons-material/Business'
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom'
import api, { apiErrorMessage } from '../api/client'
import { useSimulation } from '../api/hooks'
import type { Project, Simulation } from '../api/types'
import { formatDate, formatEUR, formatEUR2 } from '../format'
import { useAmountsHidden } from '../privacy'
import PrivacyToggle from '../components/PrivacyToggle'
import ResultsPanel from '../components/ResultsPanel'
import { Mode } from '../components/resultsMode'

const inputRows: {
  label: string
  get: (s: Simulation) => string
}[] = [
  { label: "Prix d'achat", get: (s) => formatEUR(s.purchasePrice) },
  { label: 'Montant des travaux', get: (s) => formatEUR(s.renovationCosts) },
  { label: 'Compromis', get: (s) => formatEUR(s.compromis) },
  { label: "Montant de l'apport", get: (s) => formatEUR(s.downPayment) },
  { label: 'Montant du prêt', get: (s) => formatEUR(s.loanPrincipal) },
  { label: "Taux d'intérêt", get: (s) => `${s.loanRate} %` },
  { label: 'Durée du prêt', get: (s) => `${s.loanDurationYears} ans` },
]

const rows: {
  label: string
  get: (r: import('../api/types').SimulationResults) => string
  negative?: (r: import('../api/types').SimulationResults) => boolean
  mode?: Mode
}[] = [
  { label: 'Prix / m²', get: (r) => `${formatEUR2(r.pricePerM2)} /m²` },
  { label: 'Montant frais de notaire', get: (r) => formatEUR(r.notaryFeeAmount) },
  { label: 'Loyer / an', get: (r) => formatEUR(r.annualRent), mode: Mode.Rental },
  {
    label: 'Charges d’exploitation / an',
    get: (r) => formatEUR(r.annualOperatingExpenses),
    mode: Mode.Rental,
  },
  {
    label: 'Service de la dette / an',
    get: (r) => formatEUR(r.annualDebtService),
    mode: Mode.Rental,
  },
  {
    label: 'Capital restant dû',
    get: (r) => formatEUR(r.remainingLoanCapital),
    mode: Mode.Resale,
  },
  {
    label: 'Intérêts pendant la détention',
    get: (r) => formatEUR(r.interestDuringHolding),
    mode: Mode.Resale,
  },
  {
    label: 'Indemnité de remboursement anticipé',
    get: (r) => formatEUR(r.earlyRepaymentIra),
    mode: Mode.Resale,
  },
  {
    label: 'Impôt sur la plus-value (€)',
    get: (r) => formatEUR(r.capitalGainsTaxAmount),
    negative: (r) => r.grossMargin <= 0,
    mode: Mode.Resale,
  },
]

export default function SimulationDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { data: sim, loading, error } = useSimulation(id!)
  useAmountsHidden()

  const [mode, setMode] = useState<Mode>(Mode.Rental)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [projectName, setProjectName] = useState('')
  const [projectNotes, setProjectNotes] = useState('')
  const [actionError, setActionError] = useState('')
  const [busy, setBusy] = useState(false)

  if (loading) return <CircularProgress />
  if (error) return <Alert severity="error">{error}</Alert>
  if (!sim) return null

  const readOnly = sim.role === 'reader'
  const isOwner = sim.isOwner

  const openDialog = () => {
    setProjectName(sim.name)
    setProjectNotes('')
    setActionError('')
    setDialogOpen(true)
  }

  const createProject = async () => {
    setBusy(true)
    setActionError('')
    try {
      const res = await api.post<Project>('/projects', {
        name: projectName,
        notes: projectNotes,
        simulationId: sim.id,
      })
      navigate(`/projects/${res.data.id}`)
    } catch (err) {
      setActionError(apiErrorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  const remove = async () => {
    if (!window.confirm('Supprimer cette simulation ?')) return
    try {
      await api.delete(`/simulations/${sim.id}`)
      navigate('/simulations')
    } catch (err) {
      setActionError(apiErrorMessage(err))
    }
  }

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        sx={{ mb: 3, justifyContent: 'space-between', alignItems: 'flex-start' }}
      >
        <Box>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Typography variant="h4">{sim.name}</Typography>
            <Chip
              size="small"
              label={sim.project ? 'Actif' : 'Brouillon'}
              color={sim.project ? 'success' : 'default'}
            />
          </Stack>
          {(sim.address || sim.surface > 0) && (
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
              {[sim.address, sim.surface > 0 ? `${sim.surface} m²` : null]
                .filter(Boolean)
                .join(' · ')}
            </Typography>
          )}
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Créée le {formatDate(sim.createdAt)} · mise à jour le {formatDate(sim.updatedAt)}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
          <PrivacyToggle />
          {sim.project ? (
            <Button
              variant="contained"
              startIcon={<BusinessIcon />}
              component={RouterLink}
              to={`/projects/${sim.project.id}`}
            >
              Voir le projet
            </Button>
          ) : (
            isOwner && (
              <Button variant="contained" startIcon={<AddBusinessIcon />} onClick={openDialog}>
                Créer un projet
              </Button>
            )
          )}
          {!readOnly && (
            <Button
              variant="outlined"
              color="inherit"
              startIcon={<EditIcon />}
              component={RouterLink}
              to={`/simulations/${sim.id}/edit`}
            >
              Modifier
            </Button>
          )}
          {isOwner && (
            <Button variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={remove}>
              Supprimer
            </Button>
          )}
        </Stack>
      </Stack>

      {actionError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {actionError}
        </Alert>
      )}

      <ResultsPanel r={sim.results} mode={mode} onModeChange={setMode} />

      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Détail du calcul
          </Typography>

          <Typography variant="overline" sx={{ color: 'text.secondary', display: 'block', mt: 1 }}>
            Acquisition & Financement
          </Typography>
          <Stack divider={<Divider />}>
            {inputRows.map((row) => (
              <Stack
                key={row.label}
                direction="row"
                sx={{ py: 1, justifyContent: 'space-between' }}
              >
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {row.label}
                </Typography>
                <Typography variant="body2">{row.get(sim)}</Typography>
              </Stack>
            ))}
          </Stack>

          <Typography variant="overline" sx={{ color: 'text.secondary', display: 'block', mt: 2 }}>
            Résultats
          </Typography>
          {[rows.filter((row) => !row.mode), rows.filter((row) => row.mode === mode)].map(
            (group, groupIndex) => (
              <Stack
                key={groupIndex}
                divider={<Divider />}
                sx={
                  groupIndex === 1
                    ? { mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }
                    : undefined
                }
              >
                {group.map((row) => (
                  <Stack
                    key={row.label}
                    direction="row"
                    sx={{ py: 1, justifyContent: 'space-between' }}
                  >
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {row.label}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: row.negative?.(sim.results) ? 'error.main' : undefined }}
                    >
                      {row.get(sim.results)}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            ),
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Créer un projet depuis cette simulation</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {actionError && <Alert severity="error">{actionError}</Alert>}
            <TextField
              label="Nom du projet"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              required
            />
            <TextField
              label="Notes"
              value={projectNotes}
              onChange={(e) => setProjectNotes(e.target.value)}
              multiline
              minRows={3}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Annuler</Button>
          <Button
            variant="contained"
            onClick={createProject}
            disabled={busy || !projectName.trim()}
          >
            Créer le projet
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
